// MACHINERY V2 (Stroje) — Duplicate payload builder (M2.2, §9 D4).
//
// Isolated in its own module because it carries the TWO authorised client-side
// fixes to a shipped bug, and a reviewer should be able to read the whole fix
// in one screen without a card component around it.
//
// THE BUG (live, still shipping on /machinery — deliberately NOT touched):
// components/Machinery.tsx:627 `handleDuplicate` builds a ChangeMachine payload
// that OMITS two fields the DTO already has and the create endpoint already
// persists:
//
//   1. catalogModelId — the catalog-vs-custom DISCRIMINATOR (data package §12:
//      "Discriminator = Machine.catalogModelId"). Dropping it silently converts
//      a duplicated CATALOG machine into a CUSTOM one: it loses the
//      Specifications tab, the catalog image fallback, the parent-brand
//      breadcrumb, key specs and its MIDAS readiness row. Nothing warns the
//      user; the copy just quietly becomes a lesser machine.
//   2. locationId — same omission class. The duplicate lands with no location,
//      so it drops out of every location filter/grouping and out of Worker
//      location scoping until someone edits it by hand.
//
// WHY THIS IS SAFE AND FRONTEND-ONLY: both fields already exist on
// `ChangeMachine` (models/models.ts:155,158) and the create endpoint already
// persists them — the catalog-add dialog sends both today. No backend work,
// no DTO change, no migration. Ruled in §9 D4.
//
// SCOPE: v2 only. The live handleDuplicate stays byte-untouched (§1 guardrail:
// live /machinery is unchanged until cutover).
//
// Fields deliberately NOT copied, matching the live behaviour the data package
// documents: serialNumber is forced to "" (a serial is unique to one physical
// machine), and files / taperType / the Phase-7F spec copies are left to the
// server to resolve from the catalog link.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { ChangeMachine, Machine } from "../../models/models";

/**
 * Build the create payload for "Duplikovať".
 *
 * Returns `null` when a REQUIRED reference id is missing — the same abort the
 * live handler performs (it would 400 server-side). The caller surfaces an
 * error toast rather than firing a doomed request.
 */
export const buildDuplicatePayload = (machine: Machine): ChangeMachine | null => {
  if (!machine) return null;

  const payload: ChangeMachine = {
    model: machine.model,
    // A serial number identifies ONE physical machine — never cloned.
    serialNumber: "",
    typeId: machine.type?.id,
    manufacturerId: machine.manufacturer?.id,
    customBrandName: machine.customBrandName,
    operationTypeId: machine.operationType?.id,
    axisConfigurationId: machine.axisConfiguration?.id,
    controlSystemBrandId: machine.controlSystemBrand?.id,
    automationLevelId: machine.automationLevel?.id,
    system: machine.system,
    version: machine.version,
    productionYear: machine.productionYear,
    description: machine.description,
    numberOfStations: machine.numberOfStations,
    image: machine.image,
    // ---- THE TWO D4 FIXES (absent from the live payload) ----
    // Keeps a duplicated catalog machine CATALOG-LINKED.
    catalogModelId: machine.catalogModelId,
    // Keeps the duplicate on the same site/hall.
    locationId: machine.locationId,
  };

  if (
    !payload.typeId ||
    !payload.manufacturerId ||
    !payload.operationTypeId ||
    !payload.controlSystemBrandId
  ) {
    return null;
  }

  return payload;
};

export default buildDuplicatePayload;
